import React, { useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { kibuStoryItems } from '@/modules/kibu-story/data/kibuStoryData'
import { KibuStoryModuleProps } from '@/modules/kibu-story/types/kibuStory.types'
import './KibuStoryModule.css'

const KibuStoryModule: React.FC<KibuStoryModuleProps> = ({
    title = 'The Kibu way',
    items = kibuStoryItems,
    className = '',
}) => {
    const railRef = useRef<HTMLDivElement>(null)
    // Card index a button click is currently scrolling towards. While set, the counter and
    // arrow states follow it instead of the in-flight scroll position, so the arrows update
    // the moment you click rather than when the smooth scroll finishes.
    const targetIndexRef = useRef<number | null>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const [canScrollPrevious, setCanScrollPrevious] = useState(false)
    const [canScrollNext, setCanScrollNext] = useState(items.length > 1)

    useEffect(() => {
        const rail = railRef.current
        if (!rail) return

        const updatePosition = () => {
            const cards = Array.from(rail.querySelectorAll<HTMLElement>('.kibu-story-card'))
            if (!cards.length) return
            const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0)

            const targetIndex = targetIndexRef.current
            if (targetIndex !== null) {
                const targetLeft = Math.min(cards[targetIndex]?.offsetLeft ?? 0, maxScroll)
                if (Math.abs(rail.scrollLeft - targetLeft) <= 2) {
                    targetIndexRef.current = null
                } else {
                    setActiveIndex(targetIndex)
                    setCanScrollPrevious(targetIndex > 0)
                    setCanScrollNext(targetIndex < cards.length - 1 && targetLeft < maxScroll - 2)
                    return
                }
            }

            const closestCardIndex = cards.reduce((closestIndex, card, index) => {
                const closestDistance = Math.abs(cards[closestIndex].offsetLeft - rail.scrollLeft)
                const currentDistance = Math.abs(card.offsetLeft - rail.scrollLeft)
                return currentDistance < closestDistance ? index : closestIndex
            }, 0)

            // The rail has right padding, so scrollLeft can exceed the last card's start;
            // "at the end" is decided by the last card being active, not by scroll extent.
            setActiveIndex(closestCardIndex)
            setCanScrollPrevious(rail.scrollLeft > 2)
            setCanScrollNext(closestCardIndex < cards.length - 1 && rail.scrollLeft < maxScroll - 2)
        }

        // A manual scroll (wheel, touch, drag) takes over from any pending button target.
        const cancelTarget = () => { targetIndexRef.current = null }

        updatePosition()
        rail.addEventListener('scroll', updatePosition, { passive: true })
        rail.addEventListener('wheel', cancelTarget, { passive: true })
        rail.addEventListener('touchstart', cancelTarget, { passive: true })
        rail.addEventListener('pointerdown', cancelTarget, { passive: true })
        window.addEventListener('resize', updatePosition)

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updatePosition)
            : null
        resizeObserver?.observe(rail)

        return () => {
            rail.removeEventListener('scroll', updatePosition)
            rail.removeEventListener('wheel', cancelTarget)
            rail.removeEventListener('touchstart', cancelTarget)
            rail.removeEventListener('pointerdown', cancelTarget)
            window.removeEventListener('resize', updatePosition)
            resizeObserver?.disconnect()
        }
    }, [items])

    const move = (direction: 'previous' | 'next') => {
        const rail = railRef.current
        if (!rail) return

        const cards = Array.from(rail.querySelectorAll<HTMLElement>('.kibu-story-card'))
        const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0)
        const fromIndex = targetIndexRef.current ?? activeIndex
        const targetIndex = direction === 'next'
            ? Math.min(fromIndex + 1, cards.length - 1)
            : Math.max(fromIndex - 1, 0)
        const targetLeft = cards[targetIndex]?.offsetLeft ?? 0

        targetIndexRef.current = targetIndex
        setActiveIndex(targetIndex)
        setCanScrollPrevious(targetIndex > 0)
        setCanScrollNext(targetIndex < cards.length - 1 && Math.min(targetLeft, maxScroll) < maxScroll - 2)

        rail.scrollTo({ left: targetLeft, behavior: 'smooth' })
    }

    return (
        <section className={`kibu-story-module ${className}`} aria-labelledby="kibu-story-title">
            <div className="module_container">
                <header className="kibu-story-header">
                    <div className="kibu-story-heading-copy">
                        <h2 id="kibu-story-title">{title}</h2>
                    </div>

                    <div className="kibu-story-controls" aria-label="Story navigation">
                        <span className="kibu-story-count" aria-live="polite">
                            {String(activeIndex + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
                        </span>
                        <button type="button" onClick={() => move('previous')} disabled={!canScrollPrevious} aria-label="Show previous Kibu story">
                            <ArrowLeft aria-hidden="true" />
                        </button>
                        <button type="button" onClick={() => move('next')} disabled={!canScrollNext} aria-label="Show next Kibu story">
                            <ArrowRight aria-hidden="true" />
                        </button>
                    </div>
                </header>

                <div ref={railRef} className="kibu-story-rail" role="region" aria-label="What makes Kibu different" tabIndex={0}>
                    {items.map((item, index) => (
                        <article
                            key={item.id}
                            className="kibu-story-card"
                            style={{ backgroundColor: item.backgroundColor }}
                            aria-label={`${index + 1} of ${items.length}: ${item.title}`}
                        >
                            <div className="kibu-story-image-wrap">
                                {item.mediaType === 'video' ? (
                                    <video
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        poster={item.posterUrl}
                                        aria-label={item.imageAlt}
                                        style={{ objectPosition: item.imagePosition }}
                                    >
                                        <source src={item.imageUrl} type="video/mp4" />
                                    </video>
                                ) : (
                                    <img src={item.imageUrl} alt={item.imageAlt} loading="lazy" style={{ objectPosition: item.imagePosition }} />
                                )}
                            </div>

                            <div className="kibu-story-card-copy">
                                <h3>{item.title}</h3>
                                <p className="kibu-story-description">{item.description}</p>
                            </div>

                            {item.cornerMarks && (
                                <div className="kibu-story-marks" aria-hidden="true">
                                    {item.cornerMarks.map((mark) => (
                                        mark === 'sharing' ? (
                                            <svg
                                                key={mark}
                                                className="kibu-story-mark kibu-story-mark-sharing"
                                                viewBox="0 0 128 64"
                                                fill="none"
                                                overflow="visible"
                                            >
                                                <path
                                                    d="M10 32C20 10 36 10 46 32C56 54 72 54 82 32C92 10 108 10 118 32"
                                                    stroke="currentColor"
                                                    strokeWidth="14"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        ) : mark === 'safe' ? (
                                            <svg
                                                key={mark}
                                                className="kibu-story-mark kibu-story-mark-safe"
                                                viewBox="0 0 112 104"
                                                fill="none"
                                            >
                                                <path
                                                    d="M56 10L102 94H10L56 10Z"
                                                    stroke="currentColor"
                                                    strokeWidth="18"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        ) : (
                                            <span key={mark} className={`kibu-story-mark kibu-story-mark-${mark}`} />
                                        )
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>

                <div className="kibu-story-progress" aria-hidden="true">
                    {items.map((item, index) => <span key={item.id} className={index === activeIndex ? 'is-active' : ''} />)}
                </div>
            </div>
        </section>
    )
}

export default KibuStoryModule
